import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  format,
  differenceInDays,
  isAfter,
  startOfDay,
  addDays,
  addMonths,
} from 'date-fns';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CalendarIcon,
  Check,
  Edit,
  Edit2,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import createApiClient from '@/api/axiosInstance';
import PartModal from '@/js/components/modals/PartModal';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

const formSchema = z.object({
  project_name: z.string(),
  description: z.string().min(1, 'Description is required'),
  payment_terms: z.string().optional(),
  submission_deadline: z.date({
    required_error: 'Submission deadline is required',
    invalid_type_error: 'Invalid date',
  }),
});

const RFQFormScreen = ({ setScreen, setRfqSentData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const apiClient = createApiClient(navigate, dispatch);

  const alreadySelected = location?.state?.alreadySelected;
  const partsSelected = location?.state?.partsSelected;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [project_version_id, set_project_version_id] = useState(null);
  const [parts, setParts] = useState([]);
  const [selectedParts, setSelectedParts] = useState(
    partsSelected.map((item) => ({
      part_id: item.part_id,
      part_version_id: item.part_version_id,
    })) || [],
  );

  const [descriptionEdit, setDescriptionEdit] = useState(false);

  var { projectId, projectName } = useParams();

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      project_name: projectName || '',
      description: '',
      payment_terms: '',
      submission_deadline: undefined,
    },
  });

  useEffect(() => {
    const getParts = async () => {
      try {
        const response = await apiClient.post(
          '/api/project-part/get',
          JSON.stringify({
            project_id: projectId,
          }),
        );

        const responseData = response.data;
        if (responseData?.data) {
          setParts(responseData?.data?.parts);
          set_project_version_id(
            responseData?.data?.project?.project_version_id,
          );
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.error('Error: ', error_message || error);
      }
    };

    const getShortlistedManufacturers = async () => {
      try {
        const response = await apiClient.post(
          '/api/shortlisted-manufacturer/get/manufacturer-card',
          JSON.stringify({
            project_id: projectId,
          }),
        );

        const responseData = response.data;
        if (responseData?.data) {
          console.log(responseData?.data);
          setShortlistedManu(responseData?.data);
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.error('Error: ', error_message || error);
      }
    };

    if (projectId) {
      getParts();
      getShortlistedManufacturers();
    }
  }, []);

  // Function to disable invalid dates in the calendar
  const disableInvalidDates = (date) => {
    const today = startOfDay(new Date());
    const sixMonthsFromNow = addMonths(today, 6);
    return date < today || date > sixMonthsFromNow;
  };

  const handlePartSelect = (part) => {
    setSelectedParts((prev) => {
      const exists = prev.find((p) => p.part_id === part.part_id);

      if (exists) {
        return prev.filter((p) => p.part_id !== part.part_id);
      } else {
        return [
          ...prev,
          { part_id: part.part_id, part_version_id: part.part_version_id },
        ];
      }
    });
  };

  // for Manufacturers to share RFQ
  const [shortlistedManu, setShortlistedManu] = useState([]);
  const [selectedShortlistedManu, setSelectedShortlistedManu] = useState(
    alreadySelected || [],
  );

  const handleCheckboxChange = (id) => {
    setSelectedShortlistedManu((prev) =>
      prev.includes(id)
        ? prev.filter((manuId) => manuId !== id)
        : [...prev, id],
    );
  };

  // PARTS
  const [showPartModal, setShowPartModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partFormState, setPartFormState] = useState('CREATE_PART');

  const handleAddPart = (partData) => {
    if (selectedPart !== null) {
      const updatedParts = [...parts];
      updatedParts[selectedPart.index] = partData;
      setParts(updatedParts);
    } else {
      setParts((prev) => [...prev, partData]);
    }

    // Reset modal state
    setShowPartModal(false);
    setSelectedPart(null);
  };

  const handleViewPart = (index, part) => {
    setSelectedPart({ index: index, part: part });
    setPartFormState('DISPLAY_PART_RFQ');
    setShowPartModal(true);
  };

  const onSubmit = async (values) => {
    if (selectedShortlistedManu.length === 0) {
      setError('Please select at least one manufacturer to send RFQ.');
      return;
    }

    if (selectedParts.length === 0) {
      setError('Please select at least one part.');
      return;
    }

    const formattedValues = {
      project_id: projectId,
      project_version_id: project_version_id,
      part_ids: selectedParts.map((p) => p.part_id),
      part_version_id: selectedParts.map((p) => p.part_version_id),
      payment_term: values.payment_term,
      rfq_submission_deadline: values.submission_deadline
        ? format(new Date(values.submission_deadline), 'yyyy-MM-dd')
        : '',
      status: 'OPEN',
    };

    console.log('Formatted RFQ:', formattedValues);

    try {
      const rfqResponses = await Promise.all(
        selectedShortlistedManu.map((manufacturer) =>
          apiClient.post('/api/rfq/create', {
            ...formattedValues,
            manufacturer_id: manufacturer,
          }),
        ),
      );

      // Transform rfqResponses into rfqData
      const rfqData = rfqResponses.map((response) => ({
        quoteNo: response.data.data.quote_number,
        companyName: response.data.data.company_name,
        contactPerson: response.data.data.contact_name,
      }));

      console.log('RFQs sent:', rfqData);

      setScreen('RFQ_SENT');
      setRfqSentData({
        submissionDeadline: formattedValues.rfq_submission_deadline,
        rfqData: rfqData,
      });
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      console.error('Error creating RFQ:', error_message || error);
    }
  };

  return (
    <div className="bg-white rounded-lg font-roboto mx-6">
      <div className="py-6 px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-medium">Create RFQ</h1>
        </div>

        {/* Form Section */}
        <div className="mx-24">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  e.target.tagName === 'INPUT' &&
                  e.target.tagName !== 'TEXTAREA' &&
                  !e.target.getAttribute('data-allow-enter')
                ) {
                  e.preventDefault();
                }
              }}
              className=""
            >
              {/* Initial Information */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-500">
                {/* Project Name */}
                <FormField
                  control={form.control}
                  name="project_name"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-6">
                      <FormLabel className="pt-2">Project Name</FormLabel>
                      <FormControl>
                        <h1 className="text-black font-medium ">
                          {field.value}
                        </h1>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Product Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="my-6">
                      <FormLabel className="">Description</FormLabel>
                      <FormControl>
                        <div className="relative group flex items-center transition-all ease-in-out duration-300">
                          <Textarea
                            className={`w-full min-h-[80px] transition-all ease-in-out duration-300 
                                focus:ring-0 focus-visible:ring-0 focus:outline-none border-0 
                                group-hover:w-[96%] group-hover:min-h-[80px] disabled:text-black disabled:bg-white
                                ${!descriptionEdit ? 'bg-gray-100' : ''}`}
                            placeholder="Help Manufacturers Understand your Project Better"
                            disabled={!descriptionEdit}
                            style={{
                              scrollbarWidth: 'none',
                              scrollbarColor: '#B2BBC6 #F2F4F7',
                            }}
                            {...field}
                          />
                          <div className="transition-all ease-in-out duration-200">
                            {descriptionEdit ? (
                              <div className="flex gap-1 ml-1">
                                <button
                                  className="bg-white h-8 w-8 rounded-full shadow flex items-center justify-center ml-1"
                                  onClick={() => setDescriptionEdit(false)}
                                >
                                  <Check className="h-5 w-5 text-green-500" />
                                </button>
                                <button
                                  className="bg-white h-8 w-8 rounded-full shadow flex items-center justify-center ml-1"
                                  onClick={() => setDescriptionEdit(false)}
                                >
                                  <X className="h-5 w-5" />
                                </button>
                              </div>
                            ) : (
                              <button
                                className="absolute top-6 right-0 bg-white opacity-0 scale-95 
                                           group-hover:opacity-100 group-hover:scale-100 
                                           transition-all ease-in-out duration-300 h-8 w-8 rounded-full shadow flex items-center justify-center"
                                onClick={() => setDescriptionEdit(true)}
                              >
                                <Edit2 className="h-5 w-5 text-darkPurple" />
                              </button>
                            )}
                          </div>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Parts Details Section */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-500 mt-14">
                <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-4">
                  Parts Details
                </h2>
                <p className="text-sm ml-1">
                  Select the parts you want to include in this RFQ
                </p>

                {parts && parts.length > 0 ? (
                  <>
                    <div className="mt-5 grid grid-cols-custom12 gap-4 text-black text-md border py-4 rounded-md px-2 bg-veryVeryLightPurple">
                      <div className="ml-3 col-span-1"></div>
                      <div className="col-span-3 text-center">Part</div>
                      <div className="col-span-2 text-center">Part Number</div>
                      <div className="col-span-2 text-center">
                        Part Revision
                      </div>
                      <div className="col-span-2 text-center">Quantity</div>
                      <div className="col-span-2 text-center">Action</div>
                    </div>

                    {parts.map((part, index) => (
                      <div
                        key={part.part_id}
                        className="grid grid-cols-custom12 gap-4 mt-3 items-center py-4 text-sm bg-gray-50 rounded-md px-2 bg-white shadow"
                      >
                        <div className="col-span-1">
                          <div className="col-span-1">
                            <Input
                              type="checkbox"
                              checked={selectedParts.some(
                                (p) => p.part_id === part.part_id,
                              )}
                              onChange={() => handlePartSelect(part)}
                              className="w-6 h-6 ml-6 rounded text-lightPurple accent-darkPurple transition-all duration-300 ease-in checked:scale-125"
                            />
                          </div>
                        </div>
                        <div className="flex gap-12 items-center justify-center col-span-3">
                          <div className="">
                            <img
                              src={part.image}
                              alt={part.part_name}
                              className="w-16 h-16 bg-gray-100 rounded shadow"
                            />
                          </div>
                          <div className="">{part.part_name}</div>
                        </div>

                        <div className="col-span-2 text-center">
                          {part.part_number}
                        </div>
                        <div className="col-span-2 text-center">
                          {part.part_revision}
                        </div>
                        <div className="col-span-2 text-center">
                          {part.total_parts_required}
                        </div>
                        <div className="col-span-2 flex justify-center">
                          <button
                            className={` text-purple-700 hover:bg-veryLightPurple hover:bg-opacity-10 ease-in duration-200 px-2 py-1 rounded border border-lightPurple`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleViewPart(index, part);
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="mt-10 grid grid-cols-6 gap-4 text-black text-md border py-4 rounded-md px-2 bg-veryVeryLightPurple">
                      <div className="ml-3 col-span-1 ">Image</div>
                      <div className="col-span-1 ">Part Name</div>
                      <div className="col-span-1 text-center">Part Number</div>
                      <div className="col-span-1 text-center">
                        Part Revision
                      </div>
                      <div className="col-span-1 text-center">Quantity</div>
                      <div className="col-span-1 text-center">Action</div>
                    </div>
                    <p className="text-gray-500 mt-4 text-center">
                      You have no parts available. Proceed to add a part
                    </p>
                  </>
                )}
              </div>

              {/* Payment Terms & Due Date */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-500 mt-14">
                <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-4">
                  Payment Terms & Due Date
                </h2>

                <FormField
                  control={form.control}
                  name="payment_terms"
                  render={({ field }) => (
                    <FormItem className="w-1/3 mt-7 group ease-in duration-300 space-y-1">
                      <FormLabel>Payment Terms</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                            {field.value || (
                              <span className="text-slate-500">
                                Enter Payment Terms
                              </span>
                            )}
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem
                            value="Net 30"
                            className="focus:bg-veryLightPurple focus:bg-opacity-15"
                          >
                            Net 30
                          </SelectItem>
                          <SelectItem
                            value="Net 45"
                            className="focus:bg-veryLightPurple focus:bg-opacity-15"
                          >
                            Net 45
                          </SelectItem>
                          <SelectItem
                            value="Net 60"
                            className="focus:bg-veryLightPurple focus:bg-opacity-15"
                          >
                            Net 60
                          </SelectItem>
                          <SelectItem
                            value="Custom"
                            className="focus:bg-veryLightPurple focus:bg-opacity-15"
                          >
                            Custom
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="submission_deadline"
                  render={({ field }) => (
                    <FormItem className="mt-5 w-1/3 group space-y-1">
                      <FormLabel className="text-gray-500 group-hover:text-darkPurple">
                        RFQ Submission Date
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={`w-full flex justify-between group-hover:bg-veryLightPurple group-hover:bg-opacity-15 ease-in duration-150 group-hover:text-black ${
                                field.value ? '' : 'text-slate-500'
                              } font-normal border border-gray-300`}
                            >
                              {field.value
                                ? format(field.value, 'PPP')
                                : 'Select the due date'}
                              <CalendarIcon className="ml-2 h-4 w-4 text-darkPurple" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent>
                          <Calendar
                            mode="single"
                            classNames={{
                              caption_label:
                                'text-sm font-medium text-gray-600',
                              nav: 'space-x-1 flex items-center',
                              nav_button:
                                'h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-gray-700',
                              nav_button_previous: 'absolute left-1',
                              nav_button_next: 'absolute right-1',
                              table: 'w-full border-collapse space-y-1',
                              head_row:
                                'flex bg-veryLightPurple bg-opacity-15 rounded',
                              head_cell:
                                'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] h-9 flex items-center justify-center',
                              row: 'flex w-full mt-2',
                              cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-purple-50',
                              day: 'h-9 w-9 p-0 font-normal text-gray-600 hover:bg-purple-50 hover:text-purple-700',
                              day_selected:
                                'bg-purple-100 text-purple-900 hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-200 focus:text-purple-900',
                              day_today:
                                'bg-gray-100 text-gray-900 font-semibold',
                              day_outside:
                                'text-gray-400 opacity-50 hover:bg-transparent hover:text-gray-400 hover:cursor-default',
                              day_disabled:
                                'text-gray-400 opacity-50 hover:bg-transparent cursor-not-allowed',
                              day_range_middle:
                                'aria-selected:bg-purple-50 aria-selected:text-purple-900',
                              day_hidden: 'invisible',
                            }}
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={disableInvalidDates}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Supporting Files */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-500 mt-14">
                <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-4">
                  Supporting Files
                </h2>
              </div>

              {/* Share NDA */}
              <div className="bg-gray-50 p-6 rounded-lg text-gray-500 mt-14">
                <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-4">
                  Share NDA
                </h2>
              </div>

              {/* Select Manufacturers to Share RFQ */}
              <div className="bg-gray-50 p-6 rounded mt-14">
                <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-7">
                  Select Manufacturers to Share RFQ
                </h2>

                <div className="w-full border rounded p-2">
                  {/* Header Row */}
                  <div className="grid grid-cols-3 p-2 bg-veryVeryLightPurple font-medium">
                    <div className="pl-2 col-span-2 text-center">
                      Manufacturer Name
                    </div>
                    <div className="w-full text-center">Select</div>
                  </div>

                  {/* Data Rows */}
                  <div className="grid grid-cols-3 gap-2 p-2">
                    {shortlistedManu && shortlistedManu.length > 0 ? (
                      shortlistedManu.map((item, index) => (
                        <React.Fragment key={index}>
                          <div className="mt-5 col-span-2 px-2 text-center">
                            {item?.company_name}
                          </div>
                          <div className="mt-5 flex justify-center">
                            <Input
                              type="checkbox"
                              className="w-6 h-6 rounded text-lightPurple accent-darkPurple transition-all duration-300 ease-in checked:scale-125"
                              checked={selectedShortlistedManu.includes(
                                item.manufacturer_id,
                              )}
                              onChange={() =>
                                handleCheckboxChange(item.manufacturer_id)
                              }
                            />
                          </div>
                        </React.Fragment>
                      ))
                    ) : (
                      <div className="col-span-3 flex items-center justify-center h-32 text-gray-600 font-medium text-lg">
                        You have no shortlisted manufacturer to send RFQ to.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <PartModal
                selectedPart={selectedPart}
                open={showPartModal}
                state={partFormState}
                onClose={() => {
                  setShowPartModal(false);
                  setSelectedPart(null);
                }}
                onSubmit={(data) => handleAddPart(data)}
              />

              {error && (
                <div className="w-full flex items-center justify-center mt-4 ">
                  <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-3 px-3 rounded-lg">
                    {error}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <div className="mt-6 w-full flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  className={`w-[100px] text-base text-darkPurple border-darkPurple transition ease-in-out duration-300 hover:text-darkPurple`}
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  type="submit"
                  disabled={isLoading}
                  className={`w-[150px] text-base text-white transition ease-in-out duration-300 ${
                    isLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-darkPurple hover:bg-darkerPurple'
                  } flex items-center justify-center`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save & Proceed'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default RFQFormScreen;
